import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";
import { APP_ROUTES } from "./routes";

const TermsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll the app's scroll container (not window) to top
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector(".overflow-y-auto");
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: "instant" });
      }
    });
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <Helmet>
        <title>Terms of Service — MycenAI</title>
        <meta name="description" content="MycenAI terms of service for AI-powered clinical documentation." />
        <link rel="canonical" href="https://mycenai.org/terms/" />
      </Helmet>
      <div className="mx-auto max-w-4xl px-6 py-12">
        <button
          onClick={() => navigate(APP_ROUTES.SPLASH)}
          className="mb-8 text-gray-500 text-sm hover:text-gray-900 dark:hover:text-gray-100"
        >
          ← Back
        </button>

        <h1 className="mb-8 font-bold text-4xl">MycenAI Terms of Service</h1>

        <p className="mb-6 text-gray-600 dark:text-gray-400">
          <strong>Last Updated:</strong> February 12, 2026
        </p>

        <div className="space-y-8 text-gray-800 dark:text-gray-200">
          <section className="rounded-lg border border-yellow-400 bg-yellow-50 p-6 dark:bg-yellow-900/20">
            <h2 className="mb-4 font-bold text-xl">IMPORTANT NOTICE</h2>
            <p className="font-semibold">
              THESE TERMS CONTAIN A BINDING ARBITRATION PROVISION AND CLASS ACTION WAIVER THAT APPLY TO ALL CLAIMS
              BROUGHT AGAINST MYCENAI.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">1. Agreement to Terms</h2>
            <p className="mb-4">
              By accessing MycenAI (the "Services"), you accept these Terms of Service ("Terms") and MycenAI's
              Privacy Policy. If you represent an organization, you warrant you have authority to bind that entity.
              Continued use after amendments constitutes acceptance of modified terms.
            </p>
            <p>
              We reserve the right to modify these Terms (except binding arbitration clauses) with notice. Your continued
              use of the Services after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">2. Who Can Use Our Services</h2>
            <h3 className="mb-2 font-semibold text-lg">Eligibility</h3>
            <p className="mb-4">
              You must be at least 18 years old to use MycenAI. You must provide accurate account information and
              maintain the security of your credentials. You may not share your account or maintain multiple free
              accounts.
            </p>
            <h3 className="mb-2 font-semibold text-lg">Restrictions</h3>
            <p className="mb-2">You cannot use our Services if you are:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Located in U.S. embargoed countries</li>
              <li>On government prohibited persons lists</li>
              <li>Otherwise legally prohibited from using the service</li>
            </ul>
            <p>
              Corporate accounts may be subject to organizational administrator control if using a company email address.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">3. Privacy and Security</h2>
            <h3 className="mb-2 font-semibold text-lg">Core Privacy Commitment</h3>
            <p className="mb-4 font-bold text-violet-700 dark:text-violet-400">
              "We do not use your Content to train, retrain, or fine-tune our models under any circumstances."
            </p>

            <h3 className="mb-2 font-semibold text-lg">Trusted Execution Environment (TEE)</h3>
            <p className="mb-2">Our infrastructure provides:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Hardware-isolated processing with NVIDIA GPU attestation</li>
              <li>Intel Trust Domain Extensions (TDX) for isolation</li>
              <li>TLS 1.3 encryption in transit; hardware-level encryption during AI processing (Intel TDX)</li>
              <li>AI inference isolated within hardware-secured enclaves</li>
              <li>Model verification via cryptographic proof</li>
            </ul>

            <h3 className="mb-2 font-semibold text-lg">Data Handling</h3>
            <p>
              We collect limited operational metadata for billing and abuse prevention only. Your plaintext Content is
              not stored outside secure enclaves unless you explicitly direct us to do so or as legally required.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">4. Using Our Services</h2>
            <h3 className="mb-2 font-semibold text-lg">Permitted Uses</h3>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Clinical note drafting and documentation</li>
              <li>Professional communication and productivity</li>
              <li>Research and education</li>
              <li>Business applications compliant with these Terms</li>
            </ul>

            <h3 className="mb-2 font-semibold text-lg">Prohibited Uses</h3>
            <p className="mb-2">You may not:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Engage in illegal, harmful, or abusive activities</li>
              <li>Infringe on intellectual property or privacy rights</li>
              <li>Generate harmful content (exploitation, violence, terrorism)</li>
              <li>Harass, defame, threaten, or discriminate</li>
              <li>Access data outside published APIs through automation</li>
              <li>Reverse engineer or decompile the Services</li>
              <li>Bypass security or protective measures</li>
              <li>Misrepresent AI-generated content as human-authored</li>
              <li>Use Outputs to train competing AI models</li>
              <li>Disrupt or overload the Services</li>
              <li>Engage in cryptocurrency mining or unauthorized proxy usage</li>
              <li>Tamper with attestation mechanisms</li>
              <li>
                Make legally significant decisions about individuals without adequate human oversight (employment,
                housing, credit, insurance)
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">5. Content; Feedback</h2>
            <h3 className="mb-2 font-semibold text-lg">Ownership</h3>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>
                <strong>You own all Input</strong> provided to MycenAI
              </li>
              <li>
                <strong>You own all Output</strong> generated by MycenAI
              </li>
              <li>
                <strong>You are responsible</strong> for Content legality and accuracy
              </li>
            </ul>

            <h3 className="mb-2 font-semibold text-lg">Our Use of Your Content</h3>
            <p className="mb-4">
              We receive a limited license to process your Content solely for: service provision, maintenance, security,
              improvement, legal compliance, and Terms enforcement. We do not disclose Content to third parties except
              bound subprocessors or as legally required.
            </p>

            <h3 className="mb-2 font-semibold text-lg">Output Characteristics</h3>
            <p className="mb-2">Important limitations to understand:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Outputs may not be unique (similar outputs possible for different users)</li>
              <li>Outputs may be inaccurate, misleading, biased, or offensive</li>
              <li>We assign rights to Outputs subject to third-party rights and applicable law</li>
            </ul>

            <h3 className="mb-2 font-semibold text-lg">Critical Limitations</h3>
            <div className="mb-4 rounded-lg border border-red-400 bg-red-50 p-4 dark:bg-red-900/20">
              <p className="mb-2 font-semibold text-red-800 dark:text-red-400">
                You must independently evaluate all Outputs before use:
              </p>
              <ul className="ml-6 list-disc space-y-2 text-red-800 dark:text-red-300">
                <li>
                  <strong>Not for professional advice substitution</strong> (legal, medical, financial, safety)
                </li>
                <li>
                  <strong>Cannot be sole basis for factual decisions</strong>
                </li>
                <li>
                  <strong>Not for high-impact individual decisions</strong> without human oversight (employment, housing,
                  credit, insurance, public benefits)
                </li>
                <li>
                  <strong>Not suitable for high-risk/safety-critical applications</strong> (autonomous vehicles, medical
                  devices, weapons systems)
                </li>
              </ul>
            </div>

            <h3 className="mb-2 font-semibold text-lg">Feedback</h3>
            <p>
              User-submitted feedback is non-confidential. By providing feedback, you grant us a perpetual, royalty-free,
              worldwide license to use it.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">6. Intellectual Property Rights</h2>
            <p className="mb-4">
              MycenAI retains all rights to the Services, including trademarks, interface elements, and technology. You
              receive only a non-exclusive, non-transferable, revocable license to use the Services. You may not use our
              trademarks or branding without written permission.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">7. Fees and Payments</h2>
            <h3 className="mb-2 font-semibold text-lg">Beta Access</h3>
            <p className="mb-4">
              During the beta period, MycenAI is provided free of charge. We will provide 30 days' notice before
              introducing any paid subscription plans.
            </p>

            <h3 className="mb-2 font-semibold text-lg">Future Subscriptions</h3>
            <p className="mb-2">When subscriptions are introduced:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Auto-renewal will apply unless canceled</li>
              <li>Clear terms will be presented at checkout</li>
              <li>Cancellation will be available in-app or via support</li>
              <li>Cancellations take effect at billing cycle end</li>
              <li>You must provide complete and accurate payment information</li>
              <li>Payments are generally non-refundable except where required by law</li>
              <li>You are responsible for applicable taxes</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">8. Disclaimer of Warranties</h2>
            <p className="mb-4">
              THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR
              IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the Services will be uninterrupted, error-free, or that all errors will be corrected.
              Outputs may contain inaccuracies and should not be relied upon without independent verification.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">9. Limitation of Liability</h2>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYCENAI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY
              OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p>
              Our total liability for any claims under these Terms shall not exceed the amount you paid us in the 12
              months before the claim arose (or $100 if you have not paid us).
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">10. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless MycenAI from any claims, damages, losses, liabilities, and
              expenses arising from: (a) your use of the Services, (b) your violation of these Terms, (c) your violation
              of any rights of another, or (d) your Content.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">11. Termination</h2>
            <p className="mb-4">
              We may suspend or terminate your access to the Services at any time, with or without cause, with or without
              notice. You may terminate your account at any time by discontinuing use and deleting your account.
            </p>
            <p>Upon termination, your right to use the Services ceases immediately.</p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">12. Governing Law and Dispute Resolution</h2>
            <h3 className="mb-2 font-semibold text-lg">Governing Law</h3>
            <p className="mb-4">
              These Terms shall be governed by the laws of the state of Western Australia, Australia, without regard to
              conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">13. Miscellaneous</h2>
            <p className="mb-4">
              These Terms constitute the entire agreement between you and MycenAI. If any provision is found
              unenforceable, the remaining provisions remain in effect. Our failure to enforce any right or provision does
              not constitute a waiver.
            </p>
            <p>
              You may not assign or transfer these Terms without our prior written consent. We may assign these Terms
              without restriction.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-semibold text-2xl">14. Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms, please contact us at:{" "}
              <a href="mailto:support@mycenai.org" className="text-violet-600 underline hover:text-violet-700">
                support@mycenai.org
              </a>
            </p>
          </section>

          <div className="mt-12 border-t pt-8 text-center text-gray-500 text-sm">
            <p>© 2025 MycenAI. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
